import { useState, useEffect, useContext } from "react";
import { Authorization } from "../../Context/Authorization";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthContext } from "../../Context/AuthContext";
import { logActivity } from "../../utils/LogActivity";
import Preloader from "../../shared/PreLoaders";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const fieldWrap = { marginBottom: 16 };

const CreateEcoTourism = () => {
  const Api = process.env.REACT_APP_API;

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [jfmcTypes, setJfmcTypes] = useState([]);
  const [rangeOptions, setRangeOptions] = useState([]);
  const [vfcs, setVfcs] = useState([]);
  const { userId } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    ecoTourismName: "",
    ecoTourismLocation: "",
    rangeId: "",
    jfmcType: "",
    vfcId: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
  });

  useEffect(() => {
    fetchJfmcTypes();
    fetchRanges();
  }, []);

  const fetchJfmcTypes = async () => {
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/jfmc-Types`, config);
      setJfmcTypes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRanges = async () => {
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/ranges`, config);
      setRangeOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (formData.rangeId && formData.jfmcType) {
      fetchVfcs();
    }
  }, [formData.rangeId, formData.jfmcType]);

  const fetchVfcs = async () => {
    try {
      const config = Authorization();
      const res = await axios.post(
        `${Api}/api/vfcs/vfcs-by-range`,
        { rangeId: formData.rangeId, jfmcTypeId: formData.jfmcType },
        config
      );
      setVfcs(res.data.vfcs || []);
      setErrorMessage("");
    } catch (err) {
      setVfcs([]);
      setErrorMessage("No VFC available.");
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const config = Authorization();
      await axios.post(`${Api}/api/eco-tourism`, { ...formData }, config);
      toast.success("Eco Tourism created successfully");
      await logActivity(userId, "New Eco Tourism Added", "Add Eco Tourism Page", Api);
      setFormData({
        ecoTourismName: "", ecoTourismLocation: "", rangeId: "",
        jfmcType: "", vfcId: "", accountNumber: "", ifscCode: "", bankName: "",
      });
      setVfcs([]);
    } catch (error) {
      toast.error(error.response?.data?.error || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const focusStyle = (e) => (e.target.style.borderColor = "#4CAF50");
  const blurStyle = (e) => (e.target.style.borderColor = "#E2E8F0");

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Eco Tourism</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>JFMC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Create Eco Tourism</span>
        </div>
      </div>

      {/* ── Form Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
            <i className="mdi mdi-pine-tree" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>New Eco Tourism Registration Form</span>
        </div>

        <div style={{ padding: "24px 28px" }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={fieldWrap}>
                <label htmlFor="ecoTourismName" style={labelStyle}>Eco Name</label>
                <input id="ecoTourismName" type="text" value={formData.ecoTourismName}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="ecoTourismLocation" style={labelStyle}>Eco Tourism Location</label>
                <input id="ecoTourismLocation" type="text" value={formData.ecoTourismLocation}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="rangeId" style={labelStyle}>Range</label>
                <select id="rangeId" value={formData.rangeId} onChange={handleChange} required
                  style={{ ...inputStyle, cursor: "pointer" }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select Range</option>
                  {rangeOptions.map((r) => (
                    <option key={r._id} value={r._id}>{r.rangeName}</option>
                  ))}
                </select>
              </div>

              <div style={fieldWrap}>
                <label htmlFor="jfmcType" style={labelStyle}>JFMC Type</label>
                <select id="jfmcType" value={formData.jfmcType} onChange={handleChange} required
                  style={{ ...inputStyle, cursor: "pointer" }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select JFMC Type</option>
                  {jfmcTypes.map((j) => (
                    <option key={j._id} value={j._id}>{j.jfmcTypeName}</option>
                  ))}
                </select>
              </div>

              <div style={fieldWrap}>
                <label htmlFor="vfcId" style={labelStyle}>VFC</label>
                <select id="vfcId" value={formData.vfcId} onChange={handleChange} required
                  disabled={!vfcs.length}
                  style={{ ...inputStyle, cursor: vfcs.length ? "pointer" : "not-allowed", opacity: vfcs.length ? 1 : 0.6 }}
                  onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select VFC</option>
                  {vfcs.map((v) => (
                    <option key={v._id} value={v._id}>{v.vfcName}</option>
                  ))}
                </select>
                {errorMessage && (
                  <div style={{ fontSize: 11, color: "#E53E3E", marginTop: 4 }}>{errorMessage}</div>
                )}
              </div>

              <div style={fieldWrap}>
                <label htmlFor="bankName" style={labelStyle}>Bank Name</label>
                <input id="bankName" type="text" value={formData.bankName}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="accountNumber" style={labelStyle}>Account Number</label>
                <input id="accountNumber" type="text" value={formData.accountNumber}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>

              <div style={fieldWrap}>
                <label htmlFor="ifscCode" style={labelStyle}>IFSC Code</label>
                <input id="ifscCode" type="text" value={formData.ifscCode}
                  onChange={handleChange} required style={inputStyle}
                  onFocus={focusStyle} onBlur={blurStyle} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <button type="submit" disabled={loading}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", opacity: loading ? 0.7 : 1 }}>
                <i className="mdi mdi-check" /> {loading ? "Saving..." : "Submit"}
              </button>
              <button type="reset"
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "#F7FAFC", color: "#4A5568", border: "1px solid #E2E8F0", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEcoTourism;
